/**
 * GenerateAPI - Service for AI composition generation
 */

import { APIService, APIServiceError } from './APIService';
import type { GenerateRequest, GenerateResponse } from '../../types/api';
import type { Composition } from '../../types/composition';

/**
 * API endpoint for generation
 */
const GENERATE_API_URL =
  import.meta.env.VITE_API_GENERATE_URL ||
  'https://lrii6vsy7bs6omqaqewk6dvf540cimqo.lambda-url.us-west-2.on.aws/';

/**
 * GenerateAPI service for AI composition generation
 */
export class GenerateAPI extends APIService {
  /**
   * Generate a new composition or edit existing one using AI
   * 
   * @param prompt - User prompt for generation
   * @param existingComposition - Optional existing composition for edit mode
   * @returns Generated composition
   * @throws APIServiceError if generation fails
   */
  async generate(
    prompt: string,
    existingComposition?: Composition
  ): Promise<GenerateResponse> {
    if (!prompt.trim()) {
      throw new APIServiceError('Prompt cannot be empty', 400);
    }

    const mode = existingComposition ? 'edit' : 'new';

    const request: GenerateRequest = {
      mode,
      prompt: prompt.trim(),
      ...(existingComposition && { existingComposition }),
    };

    try {
      const response = await this.fetchWithRetry(GENERATE_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        retries: 0, // Disable retries for long-running AI generation
        timeout: 300000, // 5 minute timeout for AI generation (lambda has 15min timeout)
      });

      const result = await response.json();

      // Validate response format
      if (!result || !result.channels) {
        throw new APIServiceError(
          'Invalid response format - no composition returned',
          500
        );
      }

      return result as GenerateResponse;
    } catch (error) {
      if (error instanceof APIServiceError) {
        throw error;
      }

      throw new APIServiceError(
        `Generation failed: ${(error as Error).message}`,
        500
      );
    }
  }

  /**
   * Check if generation is available (user is authenticated)
   * 
   * @returns True if generation is available
   */
  isAvailable(): boolean {
    return this.isAuthenticated();
  }
}

// Export singleton instance
export const generateAPI = new GenerateAPI();
